<template>
  <div class="assets-container">
    <el-form :inline="true" class="inline-form">
      <el-form-item label="名字搜索">
        <el-input v-model="assetSearch.name" placeholder="输入名字关键字" clearable />
      </el-form-item>
      <el-form-item label="是否有图片">
        <el-select v-model="assetSearch.hasImage" style="width: 140px">
          <el-option label="全部" value="all" />
          <el-option label="有图片" value="yes" />
          <el-option label="无图片" value="no" />
        </el-select>
      </el-form-item>
      <el-form-item label="类型">
        <el-select v-model="assetSearch.assetType" style="width: 120px">
          <el-option label="全部" value="all" />
          <el-option label="场景" value="场景" />
          <el-option label="角色" value="角色" />
          <el-option label="道具" value="道具" />
        </el-select>
      </el-form-item>
      <el-form-item label="按集筛选">
        <el-select v-model="assetSearch.episodeIndex" style="width: 140px">
          <el-option label="全部" value="all" />
          <el-option
            v-for="item in episodeIndexOptions"
            :key="item"
            :label="`第${item}集`"
            :value="item"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="批注状态">
        <el-select v-model="assetSearch.annotationStatus" style="width: 140px">
          <el-option label="全部" value="all" />
          <el-option label="有未处理批注" value="pending" />
          <el-option label="待审核批注" value="reviewing" />
          <el-option label="已处理批注" value="processed" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="handleAssetSearch">搜索</el-button>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="openAssetDialog()">新增资产</el-button>
      </el-form-item>
      <el-form-item>
        <el-button type="success" icon="el-icon-download" @click="handleExportAssets">导出</el-button>
      </el-form-item>
    </el-form>

    <el-table :data="pagedAssets" border style="width: 100%">
      <el-table-column label="名字" min-width="120" show-overflow-tooltip>
        <template slot-scope="scope">
          <span>{{ scope.row.name }}</span>
          <el-tag
            v-if="scope.row.asset_type === '角色' && scope.row.voice_timbre"
            type="success"
            size="mini"
            style="margin-left: 5px;"
          >
            {{ getSpeakerName(scope.row.voice_timbre) }}
          </el-tag>
          <el-tag
            v-if="scope.row.asset_type === '角色' && !scope.row.voice_timbre"
            type="danger"
            size="mini"
            style="margin-left: 5px;"
          >
            无音色
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="asset_type" label="类型" width="100" align="center" />
      <el-table-column label="提示词" min-width="200" show-overflow-tooltip>
        <template slot-scope="scope">
          <div class="prompt-cell">
            <span class="prompt-text">{{ formatAssetPrompt(scope.row) }}</span>
            <el-button
              type="text"
              icon="el-icon-document-copy"
              class="copy-btn"
              title="复制完整提示词"
              @click.stop="copyToClipboard(formatAssetPrompt(scope.row))"
            />
          </div>
        </template>
      </el-table-column>
      <el-table-column label="使用分集" width="220">
        <template slot-scope="scope">
          <span v-if="scope.row.used_episode_indices && scope.row.used_episode_indices.length">
            {{ scope.row.used_episode_indices.map((e) => `第${e}集`).join("、") }}
          </span>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column label="图片" width="160">
        <template slot-scope="scope">
          <el-image
            v-if="scope.row.image_url"
            :src="resolveMediaUrl(scope.row.image_url)"
            :preview-src-list="[resolveMediaUrl(scope.row.image_url)]"
            lazy
            style="width: 100px; height: 60px"
            fit="cover"
          >
            <div slot="placeholder" class="asset-image-loading">加载中...</div>
            <div slot="error" class="asset-image-loading">加载失败</div>
          </el-image>
          <span v-else>无</span>
        </template>
      </el-table-column>
      <el-table-column label="批注" min-width="200">
        <template slot-scope="scope">
          <div v-if="scope.row.annotation_text || scope.row.annotation_image_url" class="annotation-cell">
            <div class="annotation-header">
              <el-tag
                :type="scope.row.annotation_status === 'processed' ? 'success' : (scope.row.annotation_status === 'reviewing' ? 'primary' : 'warning')"
                size="mini"
                class="status-tag"
              >
                {{ scope.row.annotation_status === "processed" ? "已处理" : (scope.row.annotation_status === "reviewing" ? "待审核" : "未处理") }}
              </el-tag>
              
              <!-- 未处理状态 -> 提交审核 -->
              <el-button
                v-if="scope.row.annotation_status === 'pending' || !scope.row.annotation_status"
                type="text"
                size="mini"
                icon="el-icon-upload2"
                class="quick-action-btn"
                @click="updateAnnotationStatus(scope.row, 'reviewing', '已提交审核')"
              >
                提交审核
              </el-button>

              <!-- 待审核状态 -> 拒绝 / 通过 -->
              <template v-if="scope.row.annotation_status === 'reviewing'">
                <el-button
                  type="text"
                  size="mini"
                  icon="el-icon-close"
                  class="quick-action-btn color-danger"
                  @click="updateAnnotationStatus(scope.row, 'pending', '已拒绝')"
                >
                  拒绝
                </el-button>
                <el-button
                  type="text"
                  size="mini"
                  icon="el-icon-check"
                  class="quick-action-btn color-success"
                  @click="updateAnnotationStatus(scope.row, 'processed', '已通过')"
                >
                  通过
                </el-button>
              </template>
            </div>
            
            <div class="annotation-content">
              <el-image
                v-if="scope.row.annotation_image_url"
                :src="resolveMediaUrl(scope.row.annotation_image_url)"
                :preview-src-list="[resolveMediaUrl(scope.row.annotation_image_url)]"
                class="annotation-preview-img"
                fit="cover"
              />
              <div v-if="scope.row.annotation_text" class="annotation-full-text">
                {{ scope.row.annotation_text }}
              </div>
            </div>
          </div>
          <div v-else class="annotation-empty">
            <el-button
              type="text"
              size="mini"
              icon="el-icon-edit-outline"
              @click="openAssetDialog(scope.row)"
            >
              增加批注
            </el-button>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="220">
        <template slot-scope="scope">
          <el-button type="text" @click="openAssetDialog(scope.row)">编辑</el-button>
          <el-button type="text" @click="handleGoToGenerateAsset(scope.row)">去生成</el-button>
          <el-button type="text" @click="openRedirectDialog(scope.row)">指向另一资源</el-button>
          <el-button type="text" @click="removeAsset(scope.row.id)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>
    <div class="pagination-wrap">
      <el-pagination
        background
        layout="prev, pager, next"
        :current-page.sync="assetPage"
        :page-size="assetPageSize"
        :total="filteredAssets.length"
      />
    </div>

    <!-- Asset Form Dialog -->
    <el-dialog
      :title="assetForm.id ? '编辑资产' : '新增资产'"
      :visible.sync="assetDialogVisible"
      width="720px"
    >
      <el-form label-width="80px">
        <el-form-item label="名字">
          <el-input v-model="assetForm.name" placeholder="资产名字" />
        </el-form-item>
        <el-form-item label="类型">
          <el-select v-model="assetForm.asset_type" placeholder="选择类型" style="width: 100%">
            <el-option label="场景" value="场景" />
            <el-option label="角色" value="角色" />
            <el-option label="道具" value="道具" />
          </el-select>
        </el-form-item>
        <el-form-item label="提示词">
          <el-input v-model="assetForm.prompt" type="textarea" :rows="5" />
        </el-form-item>
        <template v-if="assetForm.asset_type === '角色'">
          <el-form-item label="音色">
            <el-select
              v-model="assetForm.voice_timbre"
              placeholder="选择音色"
              filterable
              style="width: 100%"
              @change="handleVoiceChange"
            >
              <el-option
                v-for="s in speakers"
                :key="s.id"
                :label="`${s.name} (${s.gender === 'female' ? '女' : '男'})`"
                :value="s.id"
              >
                <span style="float: left">{{ s.name }}</span>
                <span style="float: right; color: #8492a6; font-size: 13px">
                  {{ s.gender === 'female' ? '女' : '男' }}
                </span>
              </el-option>
            </el-select>
            <div v-if="selectedSpeaker" class="voice-info-hint">
              试听文本：{{ selectedSpeaker.sample_text }}
              <el-button type="text" icon="el-icon-video-play" @click="playAudition(selectedSpeaker.id)">试听</el-button>
            </div>
          </el-form-item>
        </template>
        <el-form-item label="图片">
          <el-image
            v-if="assetForm.image_url"
            :src="resolveMediaUrl(assetForm.image_url)"
            style="width: 180px; height: 120px"
            fit="cover"
            class="dialog-image-preview"
          />
          <span v-else class="media-empty-text">暂无图片</span>
          <el-upload
            :show-file-list="false"
            :http-request="handleAssetImageUpload"
            action="#"
            :disabled="assetImageUploading"
            accept="image/*"
            class="upload-inline"
          >
            <el-button size="mini" :loading="assetImageUploading">
              {{ assetImageUploading ? `上传中 ${assetImageUploadProgress}%` : "上传本地图片" }}
            </el-button>
          </el-upload>
          <el-button
            v-if="assetForm.image_url"
            size="mini"
            type="danger"
            plain
            @click="assetForm.image_url = ''"
            style="margin-left: 10px"
          >
            清空
          </el-button>
        </el-form-item>
        <el-divider content-position="left">批注信息</el-divider>
        <el-form-item label="批注文字">
          <el-input v-model="assetForm.annotation_text" type="textarea" :rows="3" placeholder="填写批注内容" />
        </el-form-item>
        <el-form-item label="批注图片">
          <el-image
            v-if="assetForm.annotation_image_url"
            :src="resolveMediaUrl(assetForm.annotation_image_url)"
            style="width: 120px; height: 80px"
            fit="cover"
            class="dialog-image-preview"
          />
          <el-upload
            :show-file-list="false"
            :http-request="handleAnnotationImageUpload"
            action="#"
            :disabled="annotationImageUploading"
            accept="image/*"
            class="upload-inline"
          >
            <el-button size="mini" :loading="annotationImageUploading">
              {{ annotationImageUploading ? "上传中..." : "上传批注图" }}
            </el-button>
          </el-upload>
          <el-button
            v-if="assetForm.annotation_image_url"
            size="mini"
            type="danger"
            plain
            @click="assetForm.annotation_image_url = ''"
            style="margin-left: 10px"
          >
            清空
          </el-button>
        </el-form-item>
        <el-form-item label="批注状态">
          <el-radio-group v-model="assetForm.annotation_status">
            <el-radio label="pending">未处理</el-radio>
            <el-radio label="reviewing">待审核</el-radio>
            <el-radio label="processed">已处理</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="assetDialogVisible = false">取消</el-button>
        <el-button type="primary" :disabled="assetImageUploading || annotationImageUploading" @click="saveAsset">保存</el-button>
      </div>
    </el-dialog>

    <!-- Redirect Asset Dialog -->
    <el-dialog title="指向另一资源" :visible.sync="redirectDialogVisible" width="500px">
      <div style="margin-bottom: 20px; color: #666; font-size: 14px;">
        将资源 <b>{{ redirectSource.name }}</b> 指向另一个资源。
        <br/><br/>
        系统将执行以下操作：
        <ol>
          <li>在所有分集摘要和分段提示词中，将 <b>@{{ redirectSource.name }}</b> 替换为新资源的名称。</li>
          <li>将原本关联此集数的链接转移到新资源。</li>
          <li>删除当前资源 <b>{{ redirectSource.name }}</b>。</li>
        </ol>
      </div>
      <el-form label-width="100px">
        <el-form-item label="目标资源">
          <el-select v-model="redirectTargetId" filterable placeholder="请选择目标资源" style="width: 100%">
            <el-option
              v-for="item in redirectTargetOptions"
              :key="item.id"
              :label="`[${item.asset_type}] ${item.name}`"
              :value="item.id"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="redirectDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="redirecting" :disabled="!redirectTargetId" @click="handleRedirectAsset">确定并执行替换</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { 
  getAssets, 
  getEpisodes, 
  createAsset, 
  updateAsset, 
  deleteAsset, 
  uploadImage, 
  redirectAsset,
  getTTSSpeakers,
  getTTSSpeakerAudition
} from "@/net/api";
import { resolveMediaUrl } from "@/utils/media";
import { EMOTION_MAP } from "@/config/emotions";

const defaultAsset = () => ({
  id: null,
  name: "",
  asset_type: "场景",
  prompt: "",
  image_url: "",
  voice_timbre: "",
  voice_emotions: [],
  annotation_text: "",
  annotation_image_url: "",
  annotation_status: "pending"
});

export default {
  name: "Assets",
  data() {
    return {
      assets: [],
      episodes: [],
      assetSearch: {
        name: "",
        hasImage: "all",
        assetType: "all",
        episodeIndex: "all",
        annotationStatus: "all"
      },
      assetForm: defaultAsset(),
      assetPage: 1,
      assetPageSize: 20,
      assetDialogVisible: false,
      assetImageUploading: false,
      assetImageUploadProgress: 0,
      annotationImageUploading: false,
      redirectDialogVisible: false,
      redirectSource: {},
      redirectTargetId: null,
      redirecting: false,
      assetImageAbortController: null,
      annotationImageAbortController: null,
       speakers: [],
      selectedEmotionNames: [],
      auditionAudio: null,
      emotionMap: EMOTION_MAP
    };
  },
  computed: {
    filteredAssets() {
      const keyword = (this.assetSearch.name || "").trim().toLowerCase();
      const minUsedEpisode = (asset) => {
        const arr = asset.used_episode_indices;
        if (!arr || !arr.length) {
          return Number.POSITIVE_INFINITY;
        }
        return Math.min(...arr);
      };
      const list = this.assets.filter((asset) => {
        const image = (asset.image_url || "").trim();
        const matchImage =
          this.assetSearch.hasImage === "all" ||
          (this.assetSearch.hasImage === "yes" && !!image) ||
          (this.assetSearch.hasImage === "no" && !image);
        const matchName =
          !keyword || (asset.name || "").toLowerCase().includes(keyword);
        const matchType =
          this.assetSearch.assetType === "all" ||
          asset.asset_type === this.assetSearch.assetType;
        return matchImage && matchName && matchType;
      });
      return list.sort((a, b) => {
        const da = minUsedEpisode(a);
        const db = minUsedEpisode(b);
        if (da !== db) {
          return da - db;
        }
        return (b.id || 0) - (a.id || 0);
      });
    },
    pagedAssets() {
      const start = (this.assetPage - 1) * this.assetPageSize;
      const end = start + this.assetPageSize;
      return this.filteredAssets.slice(start, end);
    },
    episodeIndexOptions() {
      return this.episodes.map((item) => item.episode_index).sort((a, b) => a - b);
    },
    redirectTargetOptions() {
      return this.assets.filter(a => a.id !== this.redirectSource.id);
    },
    selectedSpeaker() {
      return this.speakers.find(s => s.id === this.assetForm.voice_timbre);
    },
    isAdmin() {
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        return user.role === "admin";
      } catch (e) {
        return false;
      }
    }
  },
  created() {
    this.loadAssets();
    this.loadEpisodes();
    this.loadSpeakers();
  },
  beforeRouteLeave(to, from, next) {
    this.cancelAllUploads();
    next();
  },
  beforeDestroy() {
    this.cancelAllUploads();
  },
  methods: {
    resolveMediaUrl,
    async loadSpeakers() {
      try {
        const { data } = await getTTSSpeakers();
        this.speakers = data.data || [];
      } catch (e) {
        console.error("Failed to load speakers", e);
      }
    },
    handleVoiceChange() {
      // Keep emotions empty as we only configure the voice timbre now
      this.assetForm.voice_emotions = [];
    },
    async playAudition(speakerId) {
      if (this.auditionAudio) {
        this.auditionAudio.pause();
        this.auditionAudio = null;
      }
      try {
        const { data } = await getTTSSpeakerAudition(speakerId);
        if (data.data && data.data.url) {
          const url = this.resolveMediaUrl(data.data.url);
          this.auditionAudio = new Audio(url);
          this.auditionAudio.play();
        }
      } catch (e) {
        this.$message.error("试听失败");
      }
    },
    getSpeakerName(speakerId) {
      const speaker = this.speakers.find(s => s.id === speakerId);
      return speaker ? speaker.name : speakerId;
    },
    parseVoiceEmotions(value) {
      if (Array.isArray(value)) {
        return value
          .map((item) => ({
            id: String(item && item.id ? item.id : "").trim(),
            name: String(item && item.name ? item.name : "").trim()
          }))
          .filter((item) => item.id || item.name);
      }
      if (typeof value === "string") {
        const raw = value.trim();
        if (!raw) {
          return [];
        }
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            return parsed
              .map((item) => ({
                id: String(item && item.id ? item.id : "").trim(),
                name: String(item && item.name ? item.name : "").trim()
              }))
              .filter((item) => item.id || item.name);
          }
        } catch (e) {
          return [];
        }
      }
      return [];
    },
    buildEmotionPayload(list) {
      return (Array.isArray(list) ? list : [])
        .map((item, idx) => ({
          id: String(item && item.id ? item.id : `emotion_${idx + 1}`).trim(),
          name: String(item && item.name ? item.name : "").trim()
        }))
        .filter((item) => item.name);
    },

    formatAssetPrompt(asset) {
      let p = (asset.prompt || "").trim();
      if (asset.asset_type === "道具") {
        const name = asset.name || "";
        if (name && !p.startsWith(name)) {
          p = p ? `${name}, ${p}` : name;
        }
        if (!p.includes("白色背景")) {
          p = p ? `${p}, 白色背景` : "白色背景";
        }
      }
      return p;
    },
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.$message.success("已复制到剪贴板");
      } catch (e) {
        this.$message.error("复制失败");
      }
    },
    async loadAssets() {
      const params = {};
      if (this.assetSearch.episodeIndex !== "all") {
        params.episode_index = this.assetSearch.episodeIndex;
      }
      if (this.assetSearch.annotationStatus !== "all") {
        params.annotation_status = this.assetSearch.annotationStatus;
      }
      try {
        const { data } = await getAssets(params);
        this.assets = data;
      } catch (e) {
        this.$message.error("加载资产失败");
      }
    },
    async loadEpisodes() {
      try {
        const { data } = await getEpisodes();
        this.episodes = data;
      } catch (e) {
        console.error(e);
      }
    },
    async handleAssetSearch() {
      this.assetPage = 1;
      await this.loadAssets();
    },
    handleExportAssets() {
      if (!this.filteredAssets || this.filteredAssets.length === 0) {
        this.$message.warning("当前没有可导出的资产");
        return;
      }
      
      const exportData = this.filteredAssets.map(asset => ({
        id: asset.id,
        name: asset.name,
        type: asset.asset_type,
        prompt: asset.prompt
      }));

      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `assets_export_${new Date().getTime()}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      this.$message.success(`成功导出 ${exportData.length} 个资产`);
    },
    openAssetDialog(row) {
      this.assetForm = row ? { ...row } : defaultAsset();
      this.assetForm.voice_emotions = this.parseVoiceEmotions(this.assetForm.voice_emotions);
      this.selectedEmotionNames = this.assetForm.voice_emotions.map(e => e.name);
      this.assetDialogVisible = true;
    },
    async saveAsset() {
      if (!this.assetForm.name) {
        this.$message.warning("资产名字必填");
        return;
      }
      try {
        const payload = {
          ...this.assetForm,
          voice_emotions: this.buildEmotionPayload(this.assetForm.voice_emotions)
        };
        if (this.assetForm.id) {
          await updateAsset(this.assetForm.id, payload);
        } else {
          await createAsset(payload);
        }
        this.assetDialogVisible = false;
        await this.loadAssets();
        this.$message.success("保存成功");
      } catch (e) {
        this.$message.error("保存失败");
      }
    },
    async removeAsset(id) {
      try {
        await this.$confirm("确定删除该资产吗？", "提示", { type: "warning" });
        await deleteAsset(id);
        this.$message.success("删除成功");
        await this.loadAssets();
      } catch (e) {
        if (e !== "cancel") this.$message.error("删除失败");
      }
    },
    async handleAssetImageUpload(option) {
      if (this.assetImageAbortController) {
        this.assetImageAbortController.abort();
      }
      this.assetImageAbortController = new AbortController();
      this.assetImageUploading = true;
      this.assetImageUploadProgress = 0;
      try {
        const { data } = await uploadImage(
          option.file,
          (progress) => {
            this.assetImageUploadProgress = progress;
          },
          { signal: this.assetImageAbortController.signal }
        );
        this.assetForm.image_url = data.url;
        this.$message.success("图片上传成功");
      } catch (error) {
        if (error.name === "CanceledError" || error.name === "AbortError") {
          console.log("Asset image upload canceled");
        } else {
          this.$message.error("图片上传失败");
        }
      } finally {
        this.assetImageUploading = false;
        this.assetImageAbortController = null;
      }
    },
    async handleAnnotationImageUpload(option) {
      if (this.annotationImageAbortController) {
        this.annotationImageAbortController.abort();
      }
      this.annotationImageAbortController = new AbortController();
      this.annotationImageUploading = true;
      try {
        const { data } = await uploadImage(
          option.file,
          null,
          { signal: this.annotationImageAbortController.signal }
        );
        this.assetForm.annotation_image_url = data.url;
        this.$message.success("批注图片上传成功");
      } catch (error) {
        if (error.name === "CanceledError" || error.name === "AbortError") {
          console.log("Annotation image upload canceled");
        } else {
          this.$message.error("批注图片上传失败");
        }
      } finally {
        this.annotationImageUploading = false;
        this.annotationImageAbortController = null;
      }
    },
    cancelAllUploads() {
      if (this.assetImageAbortController) {
        this.assetImageAbortController.abort();
        this.assetImageAbortController = null;
      }
      if (this.annotationImageAbortController) {
        this.annotationImageAbortController.abort();
        this.annotationImageAbortController = null;
      }
    },
    handleGoToGenerateAsset(asset) {
      this.$router.push({
        path: "/image-tasks",
        query: {
          action: "generate",
          type: "asset",
          id: asset.id,
          name: asset.name,
          asset_type: asset.asset_type,
          prompt: asset.prompt,
          image_url: asset.image_url
        }
      });
    },
    async updateAnnotationStatus(asset, status, successMsg = "状态已更新") {
      try {
        const payload = { ...asset, annotation_status: status };
        await updateAsset(asset.id, payload);
        this.$message.success(successMsg);
        await this.loadAssets();
      } catch (e) {
        this.$message.error("更新状态失败");
      }
    },
    openRedirectDialog(asset) {
      this.redirectSource = asset;
      this.redirectTargetId = null;
      this.redirectDialogVisible = true;
    },
    async handleRedirectAsset() {
      if (!this.redirectTargetId) return;
      try {
        await this.$confirm(
          `确定要将 @${this.redirectSource.name} 全部替换为新资源并删除当前资源吗？此操作不可撤销。`,
          "资源重定向确认",
          { type: "warning" }
        );
        this.redirecting = true;
        await redirectAsset({
          source_id: this.redirectSource.id,
          target_id: this.redirectTargetId
        });
        this.$message.success("资源重定向及替换成功");
        this.redirectDialogVisible = false;
        await this.loadAssets();
      } catch (e) {
        if (e !== "cancel") {
          this.$message.error(e.response?.data?.message || "操作失败");
        }
      } finally {
        this.redirecting = false;
      }
    }
  }
};
</script>

<style scoped>
.pagination-wrap {
  margin-top: 20px;
  text-align: right;
}
.inline-form {
  margin-bottom: 20px;
}
.asset-image-loading {
  background-color: #f5f7fa;
  color: #909399;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  height: 100%;
}
.dialog-image-preview {
  display: block;
  margin-bottom: 10px;
  border: 1px solid #eee;
  border-radius: 4px;
}
.media-empty-text {
  color: #999;
  font-size: 14px;
}
.upload-inline {
  display: inline-block;
  margin-left: 0;
}
.prompt-cell {
  display: flex;
  align-items: center;
}
.prompt-text {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.copy-btn {
  margin-left: 5px;
  padding: 0;
  font-size: 14px;
}
.annotation-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.annotation-header {
  display: flex;
  align-items: center;
  gap: 10px;
}
.quick-action-btn {
  padding: 0;
  font-size: 12px;
}
.color-danger {
  color: #f56c6c;
}
.color-success {
  color: #67c23a;
}
.annotation-content {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.annotation-preview-img {
  width: 60px;
  height: 40px;
  border-radius: 4px;
  border: 1px solid #ebeef5;
  cursor: pointer;
  flex-shrink: 0;
}
.annotation-full-text {
  font-size: 13px;
  color: #606266;
  line-height: 1.5;
  word-break: break-all;
  white-space: pre-wrap;
  flex: 1;
}
.status-tag {
  align-self: flex-start;
}
.voice-info-hint {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
  line-height: 1.4;
}
.emotion-row {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  align-items: center;
}
</style>
